import { useEffect } from 'react';

/**
 * Scroll-driven parallax for the AnalyticalField.
 * Reads the field's own viewport position → p ∈ [0,1], then writes per-depth-layer
 * translate/rotate offsets as SVG `transform` attributes (robust for SVG `<g>`).
 *   - outer isopleth field drifts slowest, radar layers progressively faster
 *   - the radar recedes (fade) and tilts a few degrees as the hero leaves the viewport
 *   - driven by rAF, offscreen-paused, no-op under prefers-reduced-motion (resting
 *     state is the fully-visible premium end state).
 */
const LAYERS = {
  contours: { dy: -6 },              // outer isopleth terrain — slowest
  rings:    { dy: -12 },             // inner contour hairlines — faster
  plane:    { dy: -14, fade: 0.16 }, // the whole radar recedes as a unit
  tilt:     { rot: 2.2 },            // radar tilt (0 → ~2.2°)
  shadow:   { dy: -3 },
  baseline: { dy: -4 },              // ghost structure — subtle depth shift
  stack:    { dy: -6 },
  wire:     { dy: -8 },
  levels:   { dy: -5 },
  primary:  { dy: -10 },             // current-state surface shifts more
  nodes:    { dy: -13 },             // nodes a few px further still
};

export function useScrollParallax(rootRef, enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    const root = rootRef.current;
    if (!root || typeof window === 'undefined') return undefined;

    const els = {};
    for (const name of Object.keys(LAYERS)) els[name] = root.querySelector(`.af-${name}`);

    // scroll-tilt rotation centre = radar focal centre (from the component's data attrs)
    const tiltEl = els.tilt;
    const tc = tiltEl ? parseFloat(tiltEl.getAttribute('data-cx')) : 0;
    const ty = tiltEl ? parseFloat(tiltEl.getAttribute('data-cy')) : 0;

    let raf = 0;
    let active = true;

    const apply = () => {
      if (!active) {
        raf = requestAnimationFrame(apply);
        return;
      }
      const rect = root.getBoundingClientRect();
      if (rect.height > 0) {
        const p = Math.min(1, Math.max(0, -rect.top / (0.9 * rect.height)));
        for (const [name, cfg] of Object.entries(LAYERS)) {
          const el = els[name];
          if (!el) continue;
          if (cfg.rot != null) {
            el.setAttribute('transform', `rotate(${(cfg.rot * p).toFixed(2)} ${tc} ${ty})`);
          } else {
            const dy = cfg.dy * p;
            el.setAttribute('transform', `translate(0 ${dy.toFixed(2)})`);
            if (cfg.fade) el.style.opacity = (1 - cfg.fade * p).toFixed(3);
          }
        }
      }
      raf = requestAnimationFrame(apply);
    };

    let io;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(([entry]) => {
        active = entry.isIntersecting;
      }, { rootMargin: '200px 0px' });
      io.observe(root);
    }
    raf = requestAnimationFrame(apply);
    return () => {
      cancelAnimationFrame(raf);
      if (io) io.disconnect();
      for (const [name, cfg] of Object.entries(LAYERS)) {
        const el = els[name];
        if (!el) continue;
        if (cfg.rot != null) el.setAttribute('transform', `rotate(0 ${tc} ${ty})`);
        else {
          el.setAttribute('transform', 'translate(0 0)');
          if (cfg.fade) el.style.opacity = '';
        }
      }
    };
  }, [rootRef, enabled]);
}